import { useEffect, useRef } from 'react';

import { TEXT_STYLES } from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import HotelStayDesktop from '../HotelStayDesktop';
import HotelStayMobile from '../HotelStayMobile';
import LegalDisclaimerAccordion from '../LegalDisclaimerAccordion';

import AtypicalDealsContainer from './AtypicalDeals';
import DealsHeader from './DealsHeader';
import LoadingPrompt from './LoadingPrompt';
import NoFitOfferPrompt from './NoFitOfferPrompt';
import NoOfferPrompt from './NoOfferPrompt';
import OfferFilters from './OfferFilters';
import PartnersOffersCardsList from './PartnersOffersCardsList';
import PriceSummary from './PriceSummary';
import RoomTypeFilters from './RoomTypeFilters';

import type { OnUpdateDetailPageUrl } from './types';
import type { OnStayChange } from '../types';
import type { Configs } from '@shared/types/configs';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { ConfidenceProviders } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';

import STYLES from './HotelOffers.module.scss';

import {
  usePartnersOffersList,
  usePollingStatus,
  usePriceContext,
  useRoomFilters,
  useHotelOffersActions,
} from '@hotels-hotel-detail/data-access/hooks/useHotelOffersContext';
import { buildPricesLoadedEventProps } from '@hotels-hotel-detail/utils/hotel-offers';
import {
  buildAdditionalInfoHotelPriceStatusMessage,
  buildAdditionalInfoPricesLoadedMessage,
} from '@hotels-hotel-detail/utils/mini-events/hotels-action';
import {
  PRICE_TYPE_PER_NIGHT,
  PRICE_TYPE_TOTAL,
  TAX_AND_FEE_INCLUSION_POLICY_FEES_INCLUDED,
  TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_NOT_INCLUDED,
} from '@shared/constants/ubff-gateway';
import {
  BREAKPOINTS,
  withBpkBreakpoint,
} from '@shared/ui/common/BpkBreakpoint';
import RefreshModal from '@shared/ui/components/RefreshModal';
import SwitchItem from '@shared/ui/components/SwitchItem';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';
import {
  enablePurePrice,
  enableSubPrice,
  getSwitchLabel,
} from '@shared/utils-frontend/total-price-breakdown-service';
import {
  getCurrentQueryParam,
  updateSearchParamsUrl,
} from '@shared/utils-frontend/url/url';
import stayLength from '@shared/utils-universal/guest-stay/stay-length';
import { isBrowser } from '@shared/utils-universal/is-browser';
import {
  trafficSource,
  isExcludeGreatPrice,
  isFromHPA,
  SEO_TRAFFIC_SOURCE,
} from '@shared/utils-universal/traffic-source';

export type Props = {
  i18n: I18nService;
  miniEventTracker: MiniEventTracker;
  onStayChange: OnStayChange;
  confidenceProviders: ConfidenceProviders;
  destination: Destination;
  isMobile?: boolean;
  userContext: UserContext;
  configs: Configs;
};

const cls = cssModules(STYLES);

const HotelOffers = ({
  confidenceProviders,
  configs: { enableRoomTypeWithPrices },
  destination,
  i18n,
  isMobile,
  miniEventTracker,
  onStayChange,
  userContext,
}: Props) => {
  const { applyPriceType, applyStay } = useHotelOffersActions();
  const priceContext = usePriceContext();
  const isFinished = usePollingStatus();
  const partnersOffersList = usePartnersOffersList();
  const roomFilters = useRoomFilters();
  const curPolicyInfo = useRef(priceContext.taxAndFeeInclusionPolicy);

  const {
    hotelId,
    isFiltered,
    isSeoTraffic,
    priceType,
    searchId,
    stay,
    taxAndFeeInclusionPolicy,
  } = priceContext;

  const isEmptyOffers = partnersOffersList.length === 0;
  const hasOffers = isFinished && !isEmptyOffers;
  const hasNoOffer = isFinished && !isFiltered && isEmptyOffers;
  const hasNoFitOffer = isFinished && isFiltered && isEmptyOffers;
  const isSingleNight =
    stayLength({ checkIn: stay.checkIn, checkOut: stay.checkOut }) === 1;

  const {
    culture: { currency },
    formatDate,
  } = i18n;

  const source = getCurrentQueryParam('source');
  const updateDetailPageUrl: OnUpdateDetailPageUrl = ({ updatedStay }) => {
    updateSearchParamsUrl(
      {
        source: isSeoTraffic
          ? SEO_TRAFFIC_SOURCE
          : trafficSource({
              source,
            }),
        stay: updatedStay,
        searchCycleId: searchId,
      },
      formatDate,
    );
  };

  const isFromHpa = isFromHPA(source);

  useEffect(() => {
    if (
      priceContext.taxAndFeeInclusionPolicy &&
      curPolicyInfo.current !== priceContext.taxAndFeeInclusionPolicy
    ) {
      // to prevent UI flickering during price loading
      curPolicyInfo.current = priceContext.taxAndFeeInclusionPolicy;
    }
  }, [priceContext.taxAndFeeInclusionPolicy]);

  useEffect(() => {
    if (hasOffers) {
      // todo: buildPricesLoadedEventProps will implement partnersOffersList in the event ticket
      const pricesLoadedEventProps = buildPricesLoadedEventProps({
        currency,
        priceContext,
        partnersOffersList,
      });

      miniEventTracker.trackHotelsAction(
        ACTION_TYPE.PRICES_LOADED,
        buildAdditionalInfoPricesLoadedMessage(pricesLoadedEventProps),
      );

      // great price chokepoint event
      if (!isExcludeGreatPrice(source)) {
        miniEventTracker.trackExperimentChokepoint(
          'hotels_great_price_chokepoint',
        );
      }

      // hops chokepoint event
      miniEventTracker.trackExperimentChokepoint(
        'hotel_day_view_search_results_rendered',
      );
      const hopsChokepointName = isFromHpa
        ? 'result_render_for_hpa_traffic'
        : 'result_render_for_non_hpa_traffic';
      miniEventTracker.trackExperimentChokepoint(hopsChokepointName);

      // price comparison chokepoint event
      miniEventTracker.trackExperimentChokepoint(
        'hotel_price_comparison_experiment',
      );
    }

    if (isFinished) {
      miniEventTracker.trackHotelsAction(
        ACTION_TYPE.HOTEL_PRICES_STATUS,
        buildAdditionalInfoHotelPriceStatusMessage({
          hasRate: hasOffers,
          hotelId: priceContext.hotelId,
          isFiltered,
        }),
      );
    }
  }, [
    isFiltered,
    isFinished,
    isFromHpa,
    source,
    hasOffers,
    currency,
    partnersOffersList,
    priceContext,
    roomFilters,
    miniEventTracker,
  ]);

  const handleRefreshModalClick = () => {
    onStayChange(stay);

    applyStay(stay);
  };

  const switchPriceType = () => {
    applyPriceType(
      priceType === PRICE_TYPE_TOTAL ? PRICE_TYPE_PER_NIGHT : PRICE_TYPE_TOTAL,
    );
  };

  const isManualSearchMade = !!(isBrowser() && window.location.search);
  const { totalCostStyleVar } = userContext;

  const isTaxesNotIncluded =
    (priceContext.taxAndFeeInclusionPolicy || curPolicyInfo.current) ===
      TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_NOT_INCLUDED ||
    (priceContext.taxAndFeeInclusionPolicy || curPolicyInfo.current) ===
      TAX_AND_FEE_INCLUSION_POLICY_FEES_INCLUDED;
  const showTotalCostPriceBreakdown =
    !isMobile || isTaxesNotIncluded || enableSubPrice(totalCostStyleVar);
  const showTotalCostSwitch =
    (isTaxesNotIncluded || enablePurePrice(totalCostStyleVar)) &&
    !hasNoOffer &&
    !isSingleNight;
  const showTotalCostDesktop = showTotalCostSwitch && !isMobile;
  const showTotalCostMobile = showTotalCostSwitch && isMobile;
  const showPriceSummary = !isMobile || !showTotalCostSwitch;
  const switchLabel = getSwitchLabel({
    i18n,
    isMobile: !!isMobile,
    enableRoomTypeWithPrices,
  });

  const switchProps = {
    checked: priceType === PRICE_TYPE_TOTAL,
    label: switchLabel,
    ariaLabel: switchLabel,
    onChange: switchPriceType,
    textStyle: isMobile ? TEXT_STYLES.label3 : TEXT_STYLES.label2,
    small: !isMobile,
  };

  const applyNewMobileDesign = enableRoomTypeWithPrices && isMobile;
  const displayFilters = hasOffers || isFiltered;

  return (
    <div className={cls('HotelOffers')}>
      {!applyNewMobileDesign && <DealsHeader />}
      {!applyNewMobileDesign && (
        <AtypicalDealsContainer
          destination={destination}
          stay={stay}
          isMobile={isMobile}
        />
      )}
      <div
        className={cls(
          'HotelOffers__stay',
          showTotalCostDesktop && 'HotelOffers__stay--withSwitch',
        )}
      >
        {isMobile ? (
          <HotelStayMobile
            hotelId={hotelId}
            stay={stay}
            onStayChange={onStayChange}
            updateDetailPageUrl={updateDetailPageUrl}
            ubffEnabled
          />
        ) : (
          <HotelStayDesktop
            destination={destination}
            stay={stay}
            onStayChange={onStayChange}
            updateDetailPageUrl={updateDetailPageUrl}
          />
        )}
        {showTotalCostDesktop && <SwitchItem {...switchProps} />}
      </div>
      {applyNewMobileDesign && (
        <>
          {displayFilters && <OfferFilters />}
          <RoomTypeFilters />
          <div className={cls('HotelOffers__mobilePriceSummary')}>
            {showTotalCostMobile && <SwitchItem {...switchProps} />}
            {showPriceSummary && (
              <PriceSummary
                priceType={priceType}
                taxAndFeeInclusionPolicy={taxAndFeeInclusionPolicy}
                secondaryStyle
              />
            )}
          </div>
        </>
      )}
      <div className={cls('HotelOffers__offerContent')}>
        {!applyNewMobileDesign && (
          <>
            {showTotalCostMobile && <SwitchItem {...switchProps} />}
            {displayFilters && <OfferFilters />}
          </>
        )}
        {hasOffers && isManualSearchMade && (
          <RefreshModal
            onClick={handleRefreshModalClick}
            confidenceProviders={confidenceProviders}
          />
        )}
        {hasOffers && (
          <>
            {showPriceSummary && !applyNewMobileDesign && (
              <PriceSummary
                priceType={priceType}
                taxAndFeeInclusionPolicy={taxAndFeeInclusionPolicy}
              />
            )}
            <PartnersOffersCardsList
              isMobile={isMobile}
              showTotalCost={showTotalCostPriceBreakdown}
            />
          </>
        )}
        {!isFinished && <LoadingPrompt />}
        {hasNoOffer && <NoOfferPrompt />}
        {hasNoFitOffer && <NoFitOfferPrompt />}
      </div>
      {hasOffers && (
        <div className={cls('HotelOffers__legalDisclaimer')}>
          <LegalDisclaimerAccordion />
        </div>
      )}
    </div>
  );
};

export default withBpkBreakpoint(
  withMiniEventTracker(
    withConfiguration(withUserContext(withI18n(HotelOffers))),
  ),
  BREAKPOINTS.MOBILE,
  'isMobile',
);
